import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Container,
  Card,
  CardContent,
  Divider,
  Stack,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNotifications } from '../context/NotificationContext';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return <CheckCircleIcon color="success" />;
    case 'warning': return <WarningIcon color="warning" />;
    case 'error':   return <ErrorIcon color="error" />;
    case 'info':
    default:        return <InfoIcon color="info" />;
  }
};

const getNotificationColor = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'warning': return 'warning';
    case 'error':   return 'error';
    default:        return 'info';
  }
};

const formatTimestamp = (ts) => {
  const now = new Date();
  const diff = now - new Date(ts);
  const minutes = Math.floor(diff / 60000);
  const hours   = Math.floor(diff / 3600000);
  const days    = Math.floor(diff / 86400000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24)   return `${hours}h ago`;
  return `${days}d ago`;
};

const Notifications = () => {
  const {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications
  } = useNotifications();

  const [filter, setFilter]             = useState('all');
  const [sortBy, setSortBy]             = useState('newest');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all')    return true;
    if (filter === 'unread') return !n.read;
    if (filter === 'read')   return n.read;
    return n.category === filter;
  });

  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    const da = new Date(a.createdAt), db = new Date(b.createdAt);
    return sortBy === 'newest' ? db - da : da - db;
  });

  const handleToggleRead = (notification) => {
    if (notification.read) {
      markAsUnread(notification.id);
    } else {
      markAsRead(notification.id);
    }
  };

  const filterLabels = {
    all: 'All Notifications',
    unread: 'Unread Only',
    read: 'Read Only',
    orders: 'Orders',
    enquiries: 'Enquiries',
    offers: 'Offers',
    system: 'System'
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
            Notifications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Stay updated with your latest activities and alerts
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadNotifications}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          >
            {filterLabels[filter] || 'Filter'}
          </Button>
        </Stack>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        {Object.entries(filterLabels).map(([key, label]) => (
          <MenuItem
            key={key}
            selected={filter === key}
            onClick={() => { setFilter(key); setFilterMenuAnchor(null); }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* Action Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box>
              <Chip
                label={`${filteredNotifications.length} notification${filteredNotifications.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
              {unreadCount > 0 && (
                <Chip
                  label={`${unreadCount} unread`}
                  color="error"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>

              {unreadCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={markAllAsRead}
                >
                  Mark All Read
                </Button>
              )}

              {notifications.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={clearAllNotifications}
                >
                  Clear All
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Notifications List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : sortedNotifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filter === 'all'
              ? "You're all caught up! No notifications to display."
              : `No ${filter} notifications to display.`}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List disablePadding>
            {sortedNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                    pr: 14
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: notification.read ? 'normal' : 'bold', flexGrow: 1 }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.category}
                          size="small"
                          color={getNotificationColor(notification.type)}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatTimestamp(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {notification.message}
                      </Typography>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        edge="end"
                        aria-label={notification.read ? 'mark as unread' : 'mark as read'}
                        onClick={() => handleToggleRead(notification)}
                        size="small"
                      >
                        {notification.read ? <MarkEmailUnreadIcon /> : <MarkEmailReadIcon />}
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => deleteNotification(notification.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>

                {index < sortedNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
};

export default Notifications;
